import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { locField } from "@/lib/i18nField";
import { Layers, Plus, Save, Archive, ChevronDown } from "lucide-react";

const LANGS = ["fr", "de", "it", "en"];
const emptyCat = () => ({ key: "", order: 50, icon: "hash", active: true, locked: false, public: true, indexable: true, members_can_post: true, admin_only: false, translations: { name: { fr: "", de: "", it: "", en: "" }, description: { fr: "", de: "", it: "", en: "" } } });

export default function CommunityManager() {
  const { lang, t } = useLang();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(emptyCat());

  const { data: cats = [] } = useQuery({ queryKey: ["adminComCats"], queryFn: async () => (await api.get("/community/admin/categories")).data });

  const startEdit = (c) => { setEditing(c.key); setDraft(JSON.parse(JSON.stringify({ ...emptyCat(), ...c, translations: { name: { ...emptyCat().translations.name, ...(c.translations?.name || {}) }, description: { ...emptyCat().translations.description, ...(c.translations?.description || {}) } } }))); setCreating(false); };
  const startCreate = () => { setCreating(true); setEditing(null); setDraft(emptyCat()); };

  const save = async () => {
    try {
      if (creating) { await api.post("/community/admin/categories", draft); toast.success(t("com_saved")); }
      else { await api.put(`/community/admin/categories/${editing}`, draft); toast.success(t("com_saved")); }
      setEditing(null); setCreating(false);
      qc.invalidateQueries({ queryKey: ["adminComCats"] });
    } catch (e) { toast.error(e?.response?.data?.detail || t("com_error")); }
  };
  const archive = async (key) => { try { await api.delete(`/community/admin/categories/${key}`); qc.invalidateQueries({ queryKey: ["adminComCats"] }); } catch { toast.error(t("com_error")); } };

  const field = "bg-[#0B0C0E] border border-[#272A34] rounded-md px-3 py-2 text-sm text-stone-200 w-full";
  const editingOpen = creating || editing;

  const Toggle = ({ k, label }) => (
    <label className="flex items-center gap-2 text-xs text-stone-300"><input type="checkbox" checked={!!draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.checked })} className="w-4 h-4 accent-amber-500" data-testid={`cat-toggle-${k}`} /> {label}</label>
  );

  return (
    <section className="mt-14" data-testid="community-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-stone-100 flex items-center gap-2"><Layers className="w-5 h-5 text-amber-500" /> {t("com_admin_cats")}</h2>
        <button data-testid="cat-new-btn" onClick={startCreate} className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-4 py-2 rounded-md text-sm"><Plus className="w-4 h-4" /> {t("com_admin_new_cat")}</button>
      </div>

      {editingOpen && (
        <div className="bg-[#121418] border border-amber-800/40 rounded-lg p-5 mb-6 space-y-4" data-testid="cat-editor">
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className="block text-[10px] font-mono uppercase text-stone-500 mb-1">{t("com_admin_key")}</label><input disabled={!creating} value={draft.key} onChange={(e) => setDraft({ ...draft, key: e.target.value })} className={`${field} disabled:opacity-50`} data-testid="cat-key" /></div>
            <div><label className="block text-[10px] font-mono uppercase text-stone-500 mb-1">{t("com_admin_order")}</label><input type="number" value={draft.order} onChange={(e) => setDraft({ ...draft, order: parseInt(e.target.value) || 0 })} className={field} data-testid="cat-order" /></div>
            <div><label className="block text-[10px] font-mono uppercase text-stone-500 mb-1">{t("com_admin_icon")}</label><input value={draft.icon} onChange={(e) => setDraft({ ...draft, icon: e.target.value })} className={field} /></div>
          </div>
          <div className="flex flex-wrap gap-4">
            <Toggle k="active" label={t("com_admin_active")} /><Toggle k="public" label={t("com_admin_public")} />
            <Toggle k="indexable" label={t("com_admin_indexable")} /><Toggle k="members_can_post" label={t("com_admin_members_post")} />
            <Toggle k="admin_only" label={t("com_admin_admin_only")} /><Toggle k="locked" label={t("com_admin_lockedf")} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase text-stone-500 mb-1">{t("com_admin_translations")} — {t("com_create_post")}</label>
              {LANGS.map((l) => <div key={l} className="flex items-center gap-2 mb-1"><span className="w-8 text-[10px] font-mono uppercase text-amber-500">{l}</span><input value={draft.translations.name[l] || ""} onChange={(e) => setDraft({ ...draft, translations: { ...draft.translations, name: { ...draft.translations.name, [l]: e.target.value } } })} className={field} data-testid={`cat-name-${l}`} /></div>)}
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase text-stone-500 mb-1">{t("com_admin_translations")} — Desc</label>
              {LANGS.map((l) => <div key={l} className="flex items-center gap-2 mb-1"><span className="w-8 text-[10px] font-mono uppercase text-amber-500">{l}</span><input value={draft.translations.description[l] || ""} onChange={(e) => setDraft({ ...draft, translations: { ...draft.translations, description: { ...draft.translations.description, [l]: e.target.value } } })} className={field} /></div>)}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setEditing(null); setCreating(false); }} className="px-4 py-2 rounded-md text-sm text-stone-300 border border-[#272A34]">{t("com_cancel")}</button>
            <button data-testid="cat-save" onClick={save} className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-5 py-2 rounded-md text-sm"><Save className="w-4 h-4" /> {t("com_prefs_save")}</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {cats.map((c) => {
          const missing = LANGS.filter((l) => !c.translations?.name?.[l]);
          return (
            <div key={c.key} data-testid={`cat-row-${c.key}`} className="flex items-center gap-3 bg-[#121418] border border-[#272A34] rounded-lg p-3">
              <span className="font-mono text-xs text-stone-500 w-8">{c.order}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="font-serif text-stone-100">{locField(c.translations?.name, lang) || c.key}</span><span className="font-mono text-[10px] text-stone-600">{c.key}</span></div>
                <div className="flex flex-wrap gap-1.5 mt-1 text-[10px] font-mono uppercase">
                  {!c.active && <span className="text-red-400">off</span>}
                  {c.admin_only && <span className="text-amber-400">admin</span>}
                  {!c.members_can_post && <span className="text-stone-500">read-only</span>}
                  {missing.length > 0 && <span className="text-amber-500">{missing.join("/")} {t("com_admin_missing")}</span>}
                </div>
              </div>
              <button data-testid={`cat-edit-${c.key}`} onClick={() => startEdit(c)} className="text-xs text-amber-500 hover:text-amber-400 px-3 py-1.5">Edit</button>
              <button data-testid={`cat-archive-${c.key}`} onClick={() => archive(c.key)} className="text-xs text-stone-500 hover:text-red-400 inline-flex items-center gap-1"><Archive className="w-3.5 h-3.5" /> {t("com_admin_archive")}</button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
